import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { AgrDatePipe, AgrNumberPipe, TranslatePipe } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { Customer, SelectedLocation } from '@next/core-lib/user';
import { Barn } from 'src/app/cycle/models/barn-service.model';
import { Cycle, WDOsettingEnum } from 'src/app/cycle/models/cycle.model';

import {
  IntervalType,
  ObservationsRegistration,
  WeeklyReportValueObject,
} from '../../models/within-cycle-performance.model';

// Local type definition - Orientation is not exported from core-lib
type Orientation = 'portrait' | 'landscape';

@Component({
  selector: 'app-weekly-report-print',
  templateUrl: './weekly-report-print.component.html',
  styleUrls: ['./weekly-report-print.component.scss'],
  imports: [NgIf, NgFor, NgClass, TranslatePipe, AgrDatePipe],
})
export class WeeklyReportPrintComponent implements AfterContentChecked {
  private decimalPipe = inject(AgrNumberPipe);
  private readonly datePipe = inject(AgrDatePipe);
  private cdref = inject(ChangeDetectorRef);

  @Input() customer: Customer;
  @Input() location: SelectedLocation;
  @Input() cycle: Cycle;
  @Input() barns: Barn[];
  @Input() wdoDataSeries: DataSeries<number, number>[];
  @Input() refValSheetDataSeries: DataSeries<number, number>;
  @Input() observations: ObservationsRegistration[];

  @Output() print = new EventEmitter<void>();

  orientation: Orientation = 'landscape';
  today = new Date().valueOf();
  weekNumber = 0;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  printWeeklyReport() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT - Weekly Report - ${this.datePipe.transform(Date())}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  getBarnNames() {
    return this.barns?.map((b) => b.name).join(', ');
  }

  getBreeds() {
    return Array.from(new Set(this.cycle?.dayOldChicks?.map((doc) => doc.breed.name))).join(', ');
  }

  getWeeklyReportValue(barn: Barn, kpiName: string, digitsInfo = '1.0-2') {
    const wdoSerie = this.wdoDataSeries?.find(
      (wds) => wds?.name === `Cycle_${this.cycle?.id}_Barn_${barn?.id}`,
    );

    const dimensionValue = this.getDimensionValue(kpiName);

    const actualValue = wdoSerie?.series[kpiName][0].values?.find(
      (v) => v.dimensionValue === dimensionValue,
    )?.value;

    const referenceValueSheetValue = this.refValSheetDataSeries?.series[kpiName][0].values?.find(
      (v) => v.dimensionValue === dimensionValue,
    )?.value;

    return {
      actualValue:
        !isNaN(actualValue) && !this.isInfinity(actualValue)
          ? this.decimalPipe.transform(actualValue, digitsInfo)
          : null,
      referenceValueSheetValue: this.decimalPipe.transform(referenceValueSheetValue, digitsInfo),
    } as WeeklyReportValueObject;
  }

  isVisible(kpiName: string) {
    switch (kpiName) {
      case 'IndoorTemperature':
        return this.cycle?.observationSettings?.indoorTemperature !== WDOsettingEnum.Off;

      case 'IndoorHumidity':
        return this.cycle?.observationSettings?.indoorHumidity !== WDOsettingEnum.Off;

      case 'MortalityPercentageCumulative':
        return this.cycle?.observationSettings?.mortality !== WDOsettingEnum.Off;

      case 'FeedIntakePerChickCumulative':
        return this.cycle?.observationSettings?.feedIntake !== WDOsettingEnum.Off;

      case 'WaterIntakePerChickCumulative':
        return this.cycle?.observationSettings?.waterIntake !== WDOsettingEnum.Off;

      case 'WeightGainPerChickCumulative':
        return this.cycle?.observationSettings?.weight !== WDOsettingEnum.Off;

      case 'WaterToFeedRatioCumulative':
        return this.cycle?.observationSettings?.waterIntake !== WDOsettingEnum.Off;

      case 'FeedConversionRateCumulative':
        return (
          this.cycle?.observationSettings?.feedIntake !== WDOsettingEnum.Off &&
          this.cycle?.observationSettings?.weight !== WDOsettingEnum.Off
        );

      case 'BodyWeight':
        return this.cycle?.observationSettings?.weight !== WDOsettingEnum.Off;

      default:
        break;
    }
  }

  hasHighLight(weeklyReportValueObject: WeeklyReportValueObject) {
    if (
      !+weeklyReportValueObject.actualValue ||
      !+weeklyReportValueObject.referenceValueSheetValue
    ) {
      return false;
    }

    return (
      this.relDiff(
        +weeklyReportValueObject.actualValue,
        +weeklyReportValueObject.referenceValueSheetValue,
      ) > 10
    );
  }

  relDiff(a: number, b: number) {
    return 100 * Math.abs((a - b) / ((a + b) / 2));
  }

  getDimensionValue(kpiName: string) {
    const startDate = new Date(this.cycle?.cycleDate).valueOf();
    const endDate =
      this.cycle?.cycleEndDate !== null ? new Date(this.cycle?.cycleEndDate).valueOf() : null;

    let diffInDays: number = null;
    let todayIsAfterOrOnEndDate = false;

    if (endDate === null) {
      diffInDays = this.daysBetween(startDate, this.today);
    } else if (this.today >= endDate) {
      diffInDays = this.daysBetween(startDate, endDate);
      todayIsAfterOrOnEndDate = true;
    } else if (this.today < endDate) {
      diffInDays = this.daysBetween(startDate, this.today);
    }

    this.weekNumber = this.getLastFullWeek(diffInDays);

    if (todayIsAfterOrOnEndDate) {
      this.weekNumber = this.weekNumber + (diffInDays % 7 > 0 ? 1 : 0);
    }

    let dimensionValue = this.getLastDayOfFullWeek(this.weekNumber);

    // Actual level latest day in the week
    if (kpiName === 'IndoorTemperature') {
      dimensionValue = this.getIndoorTemperatureDimensionValue(dimensionValue);
    } else if (kpiName === 'IndoorHumidity') {
      dimensionValue = this.getIndoorHumidityDimensionValue(dimensionValue);
    }

    return dimensionValue;
  }

  daysBetween(d1: number, d2: number) {
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  }

  getLastFullWeek(days: number) {
    return Math.floor(days / 7);
  }

  getLastDayOfFullWeek(weekNumber: number) {
    return weekNumber * 7 - 1;
  }

  getIndoorTemperatureDimensionValue(dimensionValue: number): number {
    const intervalType = this.getIntervalType(this.cycle.observationSettings?.indoorTemperature);
    const startDimensionValue = this.getStartDimensionValue(intervalType, this.weekNumber);

    const filteredObservations = this.observations?.filter(
      (o) =>
        o.intervalType === intervalType &&
        o.intervalValue > startDimensionValue &&
        o.intervalValue <= dimensionValue &&
        o.indoorTemperatureInCelsius !== null,
    );

    return this.getMaxIntervalValue(filteredObservations);
  }

  getIndoorHumidityDimensionValue(dimensionValue: number): number {
    const intervalType = this.getIntervalType(this.cycle.observationSettings?.indoorHumidity);
    const startDimensionValue = this.getStartDimensionValue(intervalType, this.weekNumber);

    const filteredObservations = this.observations?.filter(
      (o) =>
        o.intervalType === intervalType &&
        o.intervalValue > startDimensionValue &&
        o.intervalValue <= dimensionValue &&
        o.indoorHumidityInPercentages !== null,
    );

    return this.getMaxIntervalValue(filteredObservations);
  }

  getMaxIntervalValue(observations: ObservationsRegistration[]): number {
    if (observations && observations.length > 0) {
      const intervalValues = observations.map((o) => o.intervalValue);
      if (intervalValues && Array.isArray(intervalValues) && intervalValues.length > 0) {
        return intervalValues.reduce((a, b) => Math.max(a, b));
      }
    }

    return -Infinity;
  }

  getIntervalType(setting: WDOsettingEnum) {
    if (setting && setting === WDOsettingEnum.PerDay) {
      return IntervalType.Day;
    }

    return IntervalType.Week;
  }

  getStartDimensionValue(intervalType: IntervalType, weekNumber: number): number {
    return intervalType === IntervalType.Day ? weekNumber * 7 - 7 : weekNumber - 1;
  }

  private isInfinity(n: number): boolean {
    return n?.toString() === 'Infinity' || n === Infinity;
  }
}
